import { useEffect, useRef, useState } from 'react';
import type { AiStatus } from '@aithora/shared';
import { useConsoleRole } from '../session/SessionContext';
import { useReadOnlyReason } from '../ui/ReadOnly';
import { sx } from '../ui/designStyles';
import { hm, pausedFacts, useAiControls } from './aiControls';

/**
 * Pause AI and its status (Console V4, Spec V4 section 16 A; design T183-206, T255-273,
 * values L35782-35829): the top bar's "AI acting" / "AI paused" chip with the waiting
 * count, AI status (one row per stop, each with its own release), the Pause AI sheet and
 * the red bar under the top bar while paused. Copied from the design's markup; every fact
 * in it comes from GET /assistant/status.
 *
 * Team roles pause Atlas in every building, and only the owner turns it back on. A
 * Facility Admin or Care Staff pauses their own building; their Facility Admin or Aithora
 * resumes it. Safety screens, crisis escalation and paging never pause.
 */

type Tone = 'PAUSED' | 'ACTING' | 'OFF';
const TONE: Record<Tone, [string, string, string]> = {
  PAUSED: ['#B3261E', 'rgba(179,38,30,.1)', '#8A1C16'],
  ACTING: ['#1F6B52', 'rgba(31,107,82,.12)', '#12503C'],
  OFF: ['#54606C', 'rgba(34,39,46,.07)', '#54606C'],
};
const BADGE = 'display:inline-flex;align-items:center;justify-content:center;min-width:18px;height:18px;padding:0 5px;box-sizing:border-box;border-radius:9px;background:#2F5C97;color:#fff;font-size:14px;font-weight:800;';

interface StopRow {
  key: string;
  name: string;
  state: Tone;
  detail: string;
  meta: string | null;
  release: { label: string; run: () => void } | null;
  lock: string | null;
}

/** The rows of AI status, in the design's words, from the server's facts. */
export function stopRows(s: AiStatus, act: { releaseTenant: () => void; releaseBuilding: (orgId?: string) => void }): StopRow[] {
  const me = s.viewer.userId;
  const who = (p: { id: string | null; name: string } | null) => (!p ? 'someone' : p.id && p.id === me ? 'you' : p.name);
  const { tenant, building } = pausedFacts(s);
  const rows: StopRow[] = [];
  rows.push(
    tenant
      ? {
          key: 'tenant',
          name: 'Atlas actions, every building',
          state: 'PAUSED',
          detail: 'Paused by ' + who(tenant.by) + ' at ' + hm(tenant.at) + ' in the ' + tenant.via + (tenant.reason ? '. Reason: ' + tenant.reason : '') + '. Atlas proposes nothing and runs nothing. Answers and Atlas keep working.',
          meta: tenant.refusedWhilePaused + ' actions refused while paused' + (tenant.ledgerRef ? ' · Truth Ledger ' + tenant.ledgerRef : ''),
          release: s.can.releaseTenant ? { label: 'Turn AI back on', run: act.releaseTenant } : null,
          lock: s.can.releaseTenant ? null : 'Only Aithora can turn it back on.',
        }
      : { key: 'tenant', name: 'Atlas actions, every building', state: 'ACTING', detail: 'Atlas acts within each role’s approvals. Nothing stopped.', meta: null, release: null, lock: null },
  );
  const bld = s.buildingName ?? 'your building';
  if (s.viewer.building) {
    rows.push(
      building
        ? {
            key: 'building',
            name: 'Building pause · ' + bld,
            state: 'PAUSED',
            detail: 'Every AI action in ' + bld + ' waits for a person. Nothing runs on its own. Atlas keeps talking.',
            meta: null,
            release: s.can.releaseBuilding ? { label: 'Resume AI actions here', run: () => act.releaseBuilding() } : null,
            lock: s.can.releaseBuilding ? null : 'Your Facility Admin or Aithora can resume it.',
          }
        : { key: 'building', name: 'Building pause · ' + bld, state: 'OFF', detail: 'AI actions in ' + bld + ' run as approved.', meta: null, release: null, lock: null },
    );
  } else if (s.viewer.team) {
    const fleet = s.buildingsPaused ?? [];
    if (!fleet.length) rows.push({ key: 'fleet', name: 'Building pauses', state: 'OFF', detail: 'No building has paused AI actions.', meta: null, release: null, lock: null });
    for (const b of fleet) {
      rows.push({
        key: 'b:' + b.id,
        name: 'Building pause · ' + b.name,
        state: 'PAUSED',
        detail: 'Every AI action in ' + b.name + ' waits for a person. Nothing runs on its own. Atlas keeps talking.',
        meta: b.by ? 'Paused by ' + who(b.by) + (b.at ? ' at ' + hm(b.at) : '') + (b.reason ? '. Reason: ' + b.reason : '') : null,
        release: s.can.releaseBuilding ? { label: 'Resume AI actions here', run: () => act.releaseBuilding(b.id) } : null,
        lock: s.can.releaseBuilding ? null : 'The building’s Facility Admin or Aithora can resume it.',
      });
    }
  }
  return rows;
}

/**
 * The chip and AI status, in the top bar before Help (design T183). Under 1180px of
 * console the design draws the dot and the waiting count only (tbWide); the words stay as
 * the chip's accessible name.
 */
export function AiChip({ wide = true }: { wide?: boolean } = {}) {
  const ai = useAiControls();
  const roReason = useReadOnlyReason();
  const ref = useRef<HTMLSpanElement>(null);
  const open = ai?.statusOpen ?? false;
  useEffect(() => {
    if (!open || !ai) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) ai.setStatusOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') ai.setStatusOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, ai]);
  const s = ai?.status.data;
  if (!ai || !ai.chipRole || !s || !s.show) return null;
  const { chipPaused } = pausedFacts(s);
  const count = ai.queueRole ? ai.approvals.data?.items.length ?? s.pending : 0;
  const team = s.viewer.team;
  const bld = s.buildingName ?? 'your building';
  const rows = stopRows(s, { releaseTenant: () => void ai.resume('tenant'), releaseBuilding: (orgId) => void ai.resume('building', orgId) });
  const toggle = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    ai.setStatusOpen(!open);
  };
  const chip = 'display:inline-flex;align-items:center;gap:7px;height:30px;padding:0 11px;border-radius:15px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;' + (chipPaused ? 'background:#B3261E;color:#fff;' : 'background:rgba(31,107,82,.1);color:#12503C;');
  const dot = 'width:7px;height:7px;border-radius:50%;background:' + (chipPaused ? '#fff' : '#1F6B52') + ';' + (chipPaused ? '' : 'animation:pulseDot 2s ease-in-out infinite;');
  return (
    <span ref={ref} style={sx('position:relative;display:inline-flex;flex:0 0 auto;')}>
      <a href="#" onClick={toggle} title="AI status" aria-expanded={open} aria-label={(chipPaused ? 'AI paused' : 'AI acting') + (count > 0 ? `, ${count} waiting for your OK` : '')} style={sx(chip)}>
        <span style={sx(dot)} aria-hidden="true"></span>
        {wide && (chipPaused ? 'AI paused' : 'AI acting')}
        {count > 0 && <span style={sx(BADGE)}>{count}</span>}
      </a>
      {open && (
        <div role="dialog" aria-label="AI status" data-screen-label="AI status" style={sx('position:absolute;top:42px;right:0;z-index:95;width:360px;background:#FCFAF4;border:1px solid rgba(34,39,46,.14);border-radius:12px;box-shadow:0 14px 40px rgba(34,39,46,.18);padding:14px;display:flex;flex-direction:column;gap:10px;animation:riseIn .18s cubic-bezier(.22,.61,.36,1);')}>
          <div style={sx('display:flex;align-items:baseline;gap:8px;')}>
            <span style={sx('font-size:14px;font-weight:800;color:#22272E;')}>AI status</span>
            <span style={sx('font-size:14px;color:#54606C;')}>{team ? 'Aithora · every building' : bld}</span>
            <span style={sx('flex:1;')}></span>
            <a href="#" onClick={toggle} style={sx('font-size:14px;font-weight:600;color:#54606C;text-decoration:none;')}>Close</a>
          </div>
          {rows.map((x) => (
            <div key={x.key} style={sx('display:flex;flex-direction:column;gap:4px;padding-top:9px;border-top:1px solid rgba(34,39,46,.08);')}>
              <div style={sx('display:flex;align-items:center;gap:8px;')}>
                <span style={sx('width:7px;height:7px;border-radius:50%;flex:0 0 7px;background:' + TONE[x.state][0] + ';')}></span>
                <span style={sx('font-size:13px;font-weight:700;color:#22272E;flex:1 1 auto;min-width:0;')}>{x.name}</span>
                <span style={sx('display:inline-flex;align-items:center;height:20px;padding:0 7px;border-radius:5px;font-size:14px;font-weight:700;letter-spacing:.06em;white-space:nowrap;color:' + TONE[x.state][2] + ';background:' + TONE[x.state][1] + ';')}>{x.state}</span>
              </div>
              <div style={sx('font-size:14px;color:#3C454F;line-height:1.5;text-wrap:pretty;')}>{x.detail}</div>
              {x.meta && <div style={sx('font-size:14px;color:#54606C;line-height:1.5;')}>{x.meta}</div>}
              {x.release && (
                <a
                  href="#"
                  aria-disabled={roReason ? true : undefined}
                  title={roReason ?? undefined}
                  onClick={(e) => { e.preventDefault(); if (!roReason) x.release!.run(); }}
                  style={sx('align-self:flex-start;display:inline-flex;align-items:center;height:30px;padding:0 12px;border-radius:8px;background:#1F6B52;color:#fff;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;')}
                >
                  {x.release.label}
                </a>
              )}
              {x.lock && <div style={sx('font-size:14px;font-weight:600;color:#54606C;')}>{x.lock}</div>}
            </div>
          ))}
          {count > 0 && (
            <a href="#" onClick={(e) => { e.preventDefault(); ai.openQueue(); }} style={sx('display:flex;align-items:center;gap:8px;padding:9px 0 0;border-top:1px solid rgba(34,39,46,.08);text-decoration:none;color:#2F5C97;font-size:13px;font-weight:700;')}>
              <span style={sx(BADGE)}>{count}</span>waiting for your OK in Atlas {'→'}
            </a>
          )}
          {s.can.pause && (
            <a
              href="#"
              aria-disabled={roReason ? true : undefined}
              title={roReason ?? undefined}
              onClick={(e) => { e.preventDefault(); if (roReason) return; ai.setStatusOpen(false); ai.setSheetOpen(true); }}
              style={sx('display:flex;align-items:center;justify-content:center;height:40px;border-radius:9px;background:#B3261E;color:#fff;font-size:13.5px;font-weight:800;letter-spacing:.02em;text-decoration:none;')}
            >
              {team ? 'Pause AI' : 'Pause AI in ' + bld}
            </a>
          )}
          {ai.error && <div role="alert" style={sx('font-size:14px;color:#B3261E;line-height:1.45;')}>{ai.error}</div>}
          <div style={sx('font-size:14px;color:#54606C;line-height:1.5;text-wrap:pretty;')}>Safety screens, crisis escalation, emergency paging and people paging people never pause. Also by text: PAUSE AI to the Aithora number.</div>
        </div>
      )}
    </span>
  );
}

/** The Pause AI sheet (design T263-273): one sheet, no PIN, an optional reason, Pause now. */
export function AiPauseSheet() {
  const ai = useAiControls();
  const { role } = useConsoleRole();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const open = !!ai?.sheetOpen;
  useEffect(() => {
    if (open) setReason('');
  }, [open]);
  useEffect(() => {
    if (!open || !ai) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') ai.setSheetOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, ai]);
  if (!ai || !open || !ai.status.data) return null;
  const s = ai.status.data;
  const team = s.viewer.team;
  const bld = s.buildingName ?? 'your building';
  const admin = role === 'ADMIN';
  const close = (e?: { preventDefault: () => void }) => {
    e?.preventDefault();
    ai.setSheetOpen(false);
  };
  const go = async (e: { preventDefault: () => void }) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    await ai.pause(reason);
    setBusy(false);
    ai.setSheetOpen(false);
  };
  return (
    <>
      <a href="#" onClick={close} aria-label="Close" style={sx('position:fixed;inset:0;z-index:300;background:rgba(34,39,46,.38);')}></a>
      <div role="dialog" aria-modal="true" aria-label="Pause the AI" data-screen-label="Pause AI sheet" style={sx('position:fixed;left:50%;top:50%;transform:translate(-50%,-50%);z-index:301;width:min(440px,92vw);box-sizing:border-box;background:#FCFAF4;border-radius:14px;box-shadow:0 20px 60px rgba(34,39,46,.3);padding:20px;display:flex;flex-direction:column;gap:12px;')}>
        <div style={sx('font-size:19px;font-weight:800;color:#22272E;letter-spacing:-.01em;')}>{team ? 'Pause the AI?' : 'Pause AI actions in ' + bld + '?'}</div>
        <div style={sx('font-size:13.5px;color:#3C454F;line-height:1.55;text-wrap:pretty;')}>
          {team
            ? 'Atlas stops taking any action, in every building. Answers and Atlas keep working. Only Aithora can turn it back on.'
            : 'Every AI action in ' + bld + ' waits for a person. Atlas keeps talking. Aithora is alerted. ' + (admin ? 'You or Aithora can resume it.' : 'Admins or Aithora resume it.')}
        </div>
        <input
          type="text"
          aria-label="Reason (optional)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason (optional)"
          style={sx('height:38px;padding:0 12px;border:1px solid rgba(34,39,46,.18);border-radius:9px;background:#FBF8F1;font:inherit;font-size:13px;color:#22272E;box-sizing:border-box;')}
        />
        <div style={sx('display:flex;align-items:center;gap:10px;')}>
          <a href="#" onClick={close} style={sx('display:inline-flex;align-items:center;height:42px;padding:0 16px;border-radius:9px;border:1px solid rgba(34,39,46,.2);color:#3C454F;font-size:13.5px;font-weight:700;text-decoration:none;')}>Cancel</a>
          <a href="#" onClick={go} aria-disabled={busy || undefined} style={sx('flex:1 1 auto;display:inline-flex;align-items:center;justify-content:center;height:42px;border-radius:9px;background:#B3261E;color:#fff;font-size:14px;font-weight:800;text-decoration:none;')}>Pause now</a>
        </div>
      </div>
    </>
  );
}

/** The red bar under the top bar on every page while paused (design T255-261). */
export function AiPausedBar() {
  const ai = useAiControls();
  const s = ai?.status.data;
  if (!ai || !s || !s.show) return null;
  const { tenant, building } = pausedFacts(s);
  const fleet = s.viewer.team ? s.buildingsPaused ?? [] : [];
  if (!tenant && !building && !fleet.length) return null;
  const me = s.viewer.userId;
  const who = tenant ? (tenant.by.id && tenant.by.id === me ? 'you' : tenant.by.name) : '';
  const head = tenant ? 'AI paused.' : building ? 'AI actions paused in ' + (s.buildingName ?? 'your building') + '.' : fleet.length === 1 ? 'AI actions paused in ' + fleet[0].name + '.' : 'AI actions paused in ' + fleet.length + ' buildings.';
  const text = tenant
    ? 'Paused by ' + who + ' at ' + hm(tenant.at) + ' in the ' + tenant.via + '. Atlas will not act until ' + (s.viewer.owner ? 'you turn' : 'Aithora turns') + ' it back on. Atlas keeps talking.'
    : building
      ? 'Every AI action here waits for a person. Atlas keeps talking.'
      : 'Every AI action there waits for a person. Atlas keeps talking.';
  return (
    <a
      href="#"
      role="status"
      onClick={(e) => { e.preventDefault(); ai.setStatusOpen(!ai.statusOpen); }}
      data-screen-label="AI paused bar"
      style={sx('flex:0 0 auto;display:flex;align-items:center;gap:10px;min-height:36px;padding:6px 20px;box-sizing:border-box;background:#F6E3E1;color:#8A1C16;border-bottom:1px solid rgba(179,38,30,.3);text-decoration:none;font-size:13px;line-height:1.4;')}
    >
      <span style={sx('width:8px;height:8px;border-radius:50%;background:#B3261E;flex:0 0 8px;')}></span>
      <span style={sx('flex:1 1 auto;min-width:0;text-wrap:pretty;')}>
        <b style={sx('font-weight:800;')}>{head}</b> {text}
      </span>
      <span style={sx('flex:0 0 auto;font-weight:700;white-space:nowrap;text-decoration:underline;')}>Details</span>
    </a>
  );
}
